import { useRef } from 'react'
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Divider,
  Button,
  Chip,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip
} from '@mui/material'
import { ExpandMore, Print } from '@mui/icons-material'
import PageProps from '../../_types/page-props.interface'
import useDocumentTitle from '../../hooks/useDocumentTitle'
import FullScreenImg from '../../components/fullscreen-img'
import PremiumSection from '../../components/premium-wrap/PremiumSection'
import ChartTabs from '../../components/premium-wrap/ChartTabs'
import YouTubeEmbed from '../../components/premium-wrap/YouTubeEmbed'
import PositionsLegend from '../../components/premium-wrap/PositionsLegend'
import PremiumNav from '../../components/premium-wrap/PremiumNav'
import PREMIUM_WRAP_NAV from '../../_constants/premium-wrap-nav.constants'
import { StyleHelper } from '../../components/style-helper'
import ScrollToTopButton from '../../components/scroll-to-top'

// Image URLs from sample.html
const IMAGES = {
  hero: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/c06dadc5-784f-4cb7-8c3b-39963cb3b939.jpg',
  sectorRotationChart: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/4d00e02f-f096-4446-a722-d2ea14f86c07.png',
  stTradingChart: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/4e894816-1257-4a69-9f81-e0b64d40b025.png',
  marketChart1: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/6a711038-6e17-401d-aae0-8cb1e2600516.png',
  marketChart2: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/1edbfc2d-802c-46b1-83a7-f3487ded3bda.png',
  heatMap: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/b6a380c9-1060-4918-ae81-dfa5c994e503.png',
  bitcoin: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/5322c97a-5272-4004-ad56-b2369e5d4091.png',
  directAxxess: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/5716b432-1327-4e50-a649-1d169a2b7b6c.jpg',
  wallStreet: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/55f7bea5-fac1-48d6-a674-87b4faa1a566.png',
  marketPosition1: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/7a76cbf0-0968-4f57-8cb6-b2fa85a89aff.png',
  marketPosition2: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/1d931772-e731-4a53-8d17-2ea2be3f1ba7.png',
  stPositions1: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/f7745d45-5b20-4d20-974c-0f6c1f91d01d.png',
  stPositions2: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/376f15a6-84f6-41a1-b263-bcaad156f4f2.png',
  stPositions3: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/f991f912-81cc-48e3-ab14-bf524430ca09.png',
  stTradingRecap: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/bd0aa093-2aaf-4207-acf0-c2d51b1a4682.png',
  sectorRotationPos1: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/3a21b3ba-954d-491f-ab33-b7d4260e04dc.png',
  sectorRotationPos2: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/9474366c-8390-44d9-9579-42f74aa0e912.png',
  sectorRotationRecap: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/2362c909-07ca-458e-8591-52e12b7172a3.png',
  systemsImg: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/8a0a1792-32a9-474a-9d0a-5b9e8ec6babc.png',
  flagImg: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/009313aa-f015-4398-9db9-eaca8fbdba3c-scaled.jpg',
  fishingImg: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/4cbffaf7-16c7-413a-9523-a33d6f7bbd21.jpg',
  qqqDaily: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/151f5b7e-2417-4e18-9263-0a6cceeca2a8.png',
  spyDaily: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/283ceb73-1ba9-4307-8a4a-a9a96c9ba8c9.png',
  iwmDaily: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/caf0fdda-9a5e-42d8-a0aa-088165c136e4.png',
  diaDaily: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/a2fe5135-c38e-47e8-9f9a-98f6c6303e6e.png',
  cnxDaily: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/a36d03b8-8c81-4fd1-9c86-a7a88cadb902.png',
  cnxWeekly: 'https://media.carnivoretrading.com/wp-content/uploads/2026/02/86fd51f5-7b2a-4468-99d1-6443d706d1dd.png'
}

export default function PremiumWrapPage(props: PageProps) {
  useDocumentTitle(props.title)
  const contentRef = useRef<HTMLDivElement>(null)

  const handlePrint = () => {
    window.print()
  }

  return (
    <Container maxWidth='xl' sx={{ pb: 8 }} ref={contentRef}>
      <Grid container spacing={3}>
        {/* Main Content Area */}
        <Grid item xs={12} lg={9}>
          {/* Header Section */}
          <Box id='premium-overview' sx={{ textAlign: 'center', mb: 4 }}>
            <Box display='flex' justifyContent='flex-end' mb={2}>
              <Tooltip title='Print Wrap'>
                <IconButton onClick={handlePrint}>
                  <Print />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant='h3' fontWeight='bold' gutterBottom>
              2-18-26
            </Typography>
            <Typography
              variant='h4'
              color='primary'
              fontWeight='bold'
              gutterBottom
            >
              CARNIVORE IS EATING THE TRADING WORLD
            </Typography>
            <Typography variant='h6' fontWeight='bold' gutterBottom>
              THE LION NOW RULES THE STREET
            </Typography>

            <StyleHelper.VerticalSpace />

            <Box
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                border: '5px solid black'
              }}
            >
              <FullScreenImg imgSrc={IMAGES.hero} />
            </Box>
          </Box>

          {/* Portfolio Performance Section */}
          <Box id='premium-portfolio-performance'>
            <PremiumSection title='Dynamic Sector Rotation Portfolio'>
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={12} md={4}>
                  <Box textAlign='center'>
                    <Typography variant='subtitle1' fontWeight='bold'>
                      SINCE INCEPTION
                    </Typography>
                    <Typography variant='h6'>22 MONTHS</Typography>
                    <Chip
                      label='+73%'
                      color='success'
                      sx={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        py: 3,
                        px: 2,
                        mt: 1
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  <FullScreenImg imgSrc={IMAGES.sectorRotationChart} />
                </Grid>
              </Grid>
            </PremiumSection>

            <PremiumSection title='Short Term Trading Model Portfolio'>
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={12} md={4}>
                  <Box textAlign='center'>
                    <Typography variant='h5' fontWeight='bold'>
                      2026 YTD = +4.98% YTD***
                    </Typography>
                    <StyleHelper.VerticalSpace />
                    <Button
                      variant='contained'
                      color='primary'
                      href='https://carnivoretrading.com/performance/'
                      target='_blank'
                    >
                      SEE OUR PERFORMANCE
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  <FullScreenImg imgSrc={IMAGES.stTradingChart} />
                </Grid>
              </Grid>
            </PremiumSection>
          </Box>

          {/* Market Overview Section */}
          <Box id='premium-market-overview'>
            <PremiumSection title='What Happened Today'>
              <ChartTabs
                tabs={[
                  { label: 'Market Charts', imgSrc: IMAGES.marketChart1 },
                  { label: 'Market Overview', imgSrc: IMAGES.marketChart2 },
                  { label: 'Heat Map', imgSrc: IMAGES.heatMap },
                  { label: 'Bitcoin', imgSrc: IMAGES.bitcoin }
                ]}
              />
            </PremiumSection>
          </Box>

          {/* Videos Section */}
          <Box id='premium-videos'>
            <PremiumSection title='Daily Video'>
              <YouTubeEmbed videoId='zjnt1fLIP2Q' title='Carnivore Daily Video' />
            </PremiumSection>
          </Box>

          {/* Promotions Section */}
          <Box id='premium-promotions'>
            {/* Direct AXXESS */}
            <Box id='premium-direct-axxess'>
              <PremiumSection title='Direct AXXESS'>
                <Box textAlign='center'>
                  <Typography variant='h5' color='error' fontWeight='bold'>
                    SIGN UP NOW
                  </Typography>
                  <Typography variant='h6' color='error' fontWeight='bold'>
                    CLICK BELOW
                  </Typography>
                  <Typography variant='h6' color='error' fontWeight='bold' gutterBottom>
                    STEP UP TO LIVE TRADING
                  </Typography>

                  <StyleHelper.VerticalSpace />

                  <Paper elevation={2} sx={{ p: 3, backgroundColor: 'rgba(226,90,11,0.05)', borderRadius: 2 }}>
                    <Typography variant='body1' fontWeight='bold' paragraph>
                      We got a great email from a Direct AXXESS gentleman the other day.
                    </Typography>
                    <Typography variant='body1' fontWeight='bold' paragraph>
                      I'll give you the summary:
                    </Typography>
                    <Typography variant='body1' fontWeight='bold' paragraph>
                      His daughter decided to dedicate herself to trading stocks.
                    </Typography>
                    <Typography variant='body1' fontWeight='bold' paragraph>
                      She's 29 years old.
                    </Typography>
                    <Typography variant='body1' fontWeight='bold' paragraph>
                      She paid attention and got on DA and let that be her guide and assistance.
                    </Typography>
                    <Typography variant='body1' fontWeight='bold' paragraph>
                      In her first 7 months, she put up a +91% return.
                    </Typography>
                    <Typography variant='body1' fontWeight='bold' fontStyle='italic' paragraph>
                      Never traded before.
                    </Typography>
                  </Paper>

                  <StyleHelper.VerticalSpace />

                  <Button
                    variant='contained'
                    color='primary'
                    size='large'
                    href='https://carnivoretrading.com/direct-axxess/'
                    target='_blank'
                  >
                    Direct AXXESS | Carnivore Trading
                  </Button>

                  <StyleHelper.VerticalSpace />

                  <FullScreenImg imgSrc={IMAGES.directAxxess} />
                </Box>
              </PremiumSection>
            </Box>

            {/* Trading App */}
            <Box id='premium-trading-app'>
              <PremiumSection title='Get The Carnivore Trading App'>
                <Box textAlign='center'>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    IT'S THE FASTEST WAY TO GET ALERTS NEXT TO DIRECT AXXESS
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    IF YOU'RE NOT ON THE APP AND SEEING THE ALERTS STREAM INSTANTLY, YOU'RE BLOWING IT.
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' gutterBottom>
                    GET THE APP HERE:
                  </Typography>
                  <Button
                    variant='outlined'
                    color='primary'
                    href='https://carnivoretrading.com/mobile-app/'
                    target='_blank'
                  >
                    App Page | Carnivore Trading
                  </Button>
                </Box>
              </PremiumSection>
            </Box>

            {/* Academy */}
            <Box id='premium-academy'>
              <PremiumSection title='Academy'>
                <Box textAlign='center'>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    IF YOU WANT TO SEE WHAT IT'S LIKE AT THE ACADEMY, CHECK THIS OUT.
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    SIGN UP NOW! OVER 90% OF THE SPOTS ARE RESERVED AND PAID FOR.
                  </Typography>
                  <Typography variant='h6' fontWeight='bold' color='primary' paragraph>
                    MARCH 22 – MARCH 26
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    CONTACT GODFATHER AND JENN ASAP AT{' '}
                    <Link href='mailto:SUPPORT@CARNIVORETRADING.COM'>
                      SUPPORT@CARNIVORETRADING.COM
                    </Link>
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    THEY WILL TREAT YOU RIGHT AND TAKE CARE OF YOU!
                  </Typography>

                  <StyleHelper.VerticalSpace />

                  <YouTubeEmbed videoId='prIPKuq8wLU' title='Academy Video' />
                </Box>
              </PremiumSection>
            </Box>
          </Box>

          {/* Market Commentary Section */}
          <Box id='premium-analysis'>
            <Box id='premium-market-commentary'>
              <PremiumSection title='Market and Strategy Related News'>
                <Typography variant='h6' fontWeight='bold' gutterBottom>
                  What Happened Today?
                </Typography>

                <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: 'rgba(0,128,0,0.05)' }}>
                  <Typography variant='body1' fontWeight='bold'>
                    The ST Trading Model Portfolio was up $8,000 today.
                  </Typography>
                  <Typography variant='body1' fontWeight='bold'>
                    We're off -21% for February.
                  </Typography>
                </Paper>

                <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: 'rgba(0,128,0,0.05)' }}>
                  <Typography variant='body1' fontWeight='bold'>
                    The Dynamic Sector Rotation Program was up $42,000 today.
                  </Typography>
                  <Typography variant='body1' fontWeight='bold'>
                    UP +73% over the last 22 months
                  </Typography>
                  <Typography variant='body1' fontWeight='bold'>
                    No margin, no options, only quality stocks.
                  </Typography>
                  <Typography variant='body1' fontWeight='bold'>
                    Smooth trading - once or twice a month changes are made
                  </Typography>
                </Paper>

                <Divider sx={{ my: 3 }} />

                <Typography variant='h6' fontWeight='bold' gutterBottom>
                  WHAT DID WE DO TODAY?
                </Typography>

                <Typography variant='body1' fontWeight='bold' paragraph>
                  Nice market follow through today until they released the Fed minutes.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Then it faded hard….everything sold off.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Some of our DA team was onto that possibility….suggesting earlier today we take profits before we learned that they've lost their minds at the Fed for sure.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  They were actually talking about how they should maybe raise rates to combat inflation….
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  What?
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  What inflation?
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  They've truly got a problem over there and it's time to end it.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Counting on you Trumpy. Hit em hard – fire the lot of that outfit.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  What a waste of money and time the Fed is.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Of course, the plan is to institute a Tariff economy, lower taxes on people, and eliminate the Fed entirely.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  What do we need them for?
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  We can print our own money
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  We don't have to give the right to someone else that then calls is a debt we have to pay
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Then tax us using that as an excuse.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  It's just a big con.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Time to end it.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  What did we do?
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Seemed a bit chaotic, with selling some ONDS and RCAT and then buying it back later in the day eve4n bigger.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  But – that's not as psychotic as it appears to most people.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Things change FAST on the Street.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Information comes in all the time, and the market reacts.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  We learned that there's a strong possibility that Iran gets hit soon.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Very soon.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Might be BS – we went that route before and the drone stocks backed up on us hard
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  But we're seeing insane bids under them now, and there's probably a good reason for it over and above the fact that they were oversold.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  We took some profits.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  The trading log is below.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  The most decisive thing we did was we're back big on the drone trade for two reasons.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  First, it's strong as hell, all the stocks in the group are showing great RSI
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Secondly, the rumor is we're gonna hit Iran shortly.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Like with bombs and such.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Which is fine with me, and should pop all the defense stocks and drones.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Might be an oil trade in there, but we're not good enough to know which way price of oil will go.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  But gold is a good trade.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  So we strapped a little NUGT into the portfolio.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  We were up $8,000 today in the ST Portfolio +3.3% on the day
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  We were up $42,000 in the Sector rotation Portfolio. +2.31% on the day.
                </Typography>

                <Paper elevation={2} sx={{ p: 2, my: 2, backgroundColor: 'rgba(255,0,0,0.05)', border: '1px solid rgba(255,0,0,0.2)' }}>
                  <Typography variant='h6' fontWeight='bold' color='error'>
                    WARNING
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    If you look at the index charts, you have to admit that it's possible that the past two days have been a little weak-ass rally into a market that is rolling over.
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    We hope that isn't the case, but Dutch is feeling strongly that this is what it was.
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    The big reason is that the rally this morning didn't hold worth a crap.
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    The rally was nice for a few hours, then it faded all day.
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    This isn't good.
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    Unless we get a catalyst of some sort, like maybe NVDA earnings….and a low PCE number on Friday….
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    Ahh what the hell.
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    NVDA is gonna crush it, and the PCE will come in light dialing back inflation fears – not that the Fed will ever…I mean EVER lower rates again.
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    Those bastards are just a waste of breath.
                  </Typography>
                  <Typography variant='body1' fontWeight='bold'>
                    I think most of us on Wall Street have had quite enough of Powell and his circus of idiots
                  </Typography>
                </Paper>

                <Divider sx={{ my: 3 }} />

                <Typography variant='body1' fontWeight='bold' paragraph>
                  See you in the morning.
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant='body1' fontWeight='bold' paragraph>
                  Direct AXXESS
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  There's nothing like it for making real money in the markets.
                </Typography>
                <Button
                  variant='contained'
                  color='primary'
                  href='https://carnivoretrading.com/direct-axxess/'
                  target='_blank'
                  sx={{ mb: 2 }}
                >
                  Direct AXXESS | Carnivore Trading
                </Button>

                <Divider sx={{ my: 2 }} />

                <Typography variant='body1' fontWeight='bold' paragraph>
                  We love the Carnivores!
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Have a great night and – as always…
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Stay Frosty
                </Typography>

                <StyleHelper.VerticalSpace />

                <FullScreenImg imgSrc={IMAGES.wallStreet} />
              </PremiumSection>
            </Box>

            {/* Market Position */}
            <Box id='premium-market-position'>
              <PremiumSection title='Where Are We In The Market?'>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FullScreenImg imgSrc={IMAGES.marketPosition1} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FullScreenImg imgSrc={IMAGES.marketPosition2} />
                  </Grid>
                </Grid>
              </PremiumSection>
            </Box>
          </Box>

          {/* Trading Section */}
          <Box id='premium-trading'>
            {/* Short Term Strategy */}
            <Box id='premium-short-term'>
              <PremiumSection title='Carnivore Short Term Swing Trading Strategy***'>
                <Typography variant='h6' color='primary' fontWeight='bold' textAlign='center'>
                  Current Live Positions and Data
                </Typography>
                <Typography variant='subtitle1' color='primary' fontWeight='bold' textAlign='center' gutterBottom>
                  Swing Trades/Active Account - Model Portfolio
                </Typography>
                <Typography variant='h6' fontWeight='bold' textAlign='center' gutterBottom>
                  Up 4.98% YTD
                </Typography>

                <StyleHelper.VerticalSpace />

                <PositionsLegend />

                <StyleHelper.VerticalSpace />

                <ChartTabs
                  tabs={[
                    { label: 'Positions 1', imgSrc: IMAGES.stPositions1 },
                    { label: 'Positions 2', imgSrc: IMAGES.stPositions2 },
                    { label: 'Positions 3', imgSrc: IMAGES.stPositions3 }
                  ]}
                />

                <StyleHelper.DoubleVerticalSpace />

                <Typography variant='h6' fontWeight='bold' gutterBottom>
                  TRADING RECAP
                </Typography>
                <FullScreenImg imgSrc={IMAGES.stTradingRecap} />
              </PremiumSection>
            </Box>

            {/* Sector Rotation */}
            <Box id='premium-sector-rotation'>
              <PremiumSection title='Sector Rotation Strategy'>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  The Sector Rotation Strategy was implemented April, 2024
                </Typography>
                <Chip
                  label='+73% Inception to Date (22 Months)'
                  color='success'
                  sx={{ fontWeight: 'bold', mb: 2 }}
                />

                <Typography variant='h6' fontWeight='bold' gutterBottom>
                  DYNAMIC SECTOR ROTATION STRATEGY
                </Typography>
                <Typography variant='body1' fontStyle='italic' gutterBottom>
                  Trades are made once a week on Friday 10AM ET
                </Typography>
                <Typography variant='body1' fontStyle='italic' gutterBottom>
                  LAUNCHED 4/1/24
                </Typography>

                <StyleHelper.VerticalSpace />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FullScreenImg imgSrc={IMAGES.sectorRotationPos1} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FullScreenImg imgSrc={IMAGES.sectorRotationPos2} />
                  </Grid>
                </Grid>

                <StyleHelper.VerticalSpace />

                <Typography variant='h6' fontWeight='bold' gutterBottom>
                  TRADING RECAP
                </Typography>
                <Typography variant='body1' fontWeight='bold' gutterBottom>
                  The below trades were made on Friday, 10AM ET
                </Typography>
                <FullScreenImg imgSrc={IMAGES.sectorRotationRecap} />

                <StyleHelper.DoubleVerticalSpace />

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography fontWeight='bold'>Financial Advisors Information</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant='body1' fontStyle='italic' paragraph>
                      Financial Advisors are using this Sector Rotation Strategy with their client accounts to build their business while also differentiating themselves within the world of Financial Advisors. It's diversified enough, there is no day trading, the time involved is minimal, and it's compliance friendly.
                    </Typography>
                    <Typography variant='body1' fontStyle='italic' paragraph>
                      The quality of the stocks is good, there is no use of margin or options, and explaining how it works is simple and easy.
                    </Typography>
                    <Typography variant='body1' fontStyle='italic' paragraph>
                      And now – with a track record of over 3% average monthly return, the clients love it, and they can double the asset base – and fees – every couple years we believe.
                    </Typography>
                    <Typography variant='body1' fontWeight='bold' paragraph>
                      Remember, our goal is to deliver around 2%-3% a month using this relaxed strategy here.
                    </Typography>
                    <Typography variant='body1' fontWeight='bold' paragraph>
                      It will take more patience than the swing trading portfolio, but there's benefits to that for sure.
                    </Typography>
                    <Typography variant='body1' fontWeight='bold' paragraph>
                      Everyone can follow this strategy and nobody need be left behind.
                    </Typography>
                    <Typography variant='body1' fontWeight='bold' paragraph>
                      Trade for 10 minutes every Friday, and then go have fun.
                    </Typography>
                    <Typography variant='body1' fontStyle='italic' paragraph>
                      As always, this portfolio is nearly always 100% invested and the ability to follow this is supposed to be very simple and easy.
                    </Typography>
                    <Typography variant='body1' fontStyle='italic' paragraph>
                      The key is to know when to leave a sector and start a new one, and which stocks we choose to sell and which we choose to add to.
                    </Typography>
                    <Typography variant='body1' fontStyle='italic' paragraph>
                      Although we let the market tell us what to do in this area, this is the value we add we believe, and therefore, it remains important that one remain engaged in what's happening here.
                    </Typography>
                    <Typography variant='body1' fontStyle='italic'>
                      Once a week on Friday, spending 15 minutes making a few changes prior to the close of trading, is all it takes.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <StyleHelper.VerticalSpace />

                <FullScreenImg imgSrc={IMAGES.systemsImg} />

                <StyleHelper.VerticalSpace />

                <Typography variant='body1' fontWeight='bold'>
                  Glad you're here.
                </Typography>
                <Typography variant='body1' fontWeight='bold'>
                  Stay Frosty.
                </Typography>
              </PremiumSection>
            </Box>

            {/* Long Term Trades */}
            <Box id='premium-long-term'>
              <PremiumSection title='Long Term Trades 2/17/26'>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 2, backgroundColor: 'rgba(255,0,0,0.05)', borderLeft: '4px solid red' }}>
                      <Typography variant='h6' fontWeight='bold' color='error'>
                        Sell QS
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 2, backgroundColor: 'rgba(0,128,0,0.05)', borderLeft: '4px solid green' }}>
                      <Typography variant='h6' fontWeight='bold' color='success.main'>
                        Buy DOCN
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <StyleHelper.VerticalSpace />

                <Typography variant='body1' fontWeight='bold' paragraph>
                  The website will be updated soon.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  We have 25 positions on. You should be allocating around 4% per name.
                </Typography>
                <Typography variant='body1' fontWeight='bold'>
                  Over 70% of the Long Term Trades have been winnners.
                </Typography>
              </PremiumSection>
            </Box>
          </Box>

          {/* Research Section */}
          <Box id='premium-research'>
            {/* About Our Systems */}
            <Box id='premium-systems'>
              <PremiumSection title='About Our Systems'>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Our multiple systems perform stock selection, pattern recognition, and trade timing for us.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  And we think our systems, combined with our personal experience and knowledge, are responsible for generating superior returns…consistently.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Anyone can deliver a great year now and then.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  We do it every year, every month, and just about every day.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  Our proprietary BITE system is the only system we know of on Wall Street that can deliver not only a price target but also a definitive time frame for it to occur using imbedded pattern recognition and trend following algorithms. Most firms research will deliver a price target and a vague time frame, such as "1 year". Our systems find explosive stock opportunities for traders with strong fundamental growth characteristics and quality market caps, earnings, liquidity, and more.
                </Typography>
                <Typography variant='body1' fontWeight='bold' paragraph>
                  BITE works hand in hand with Vector Nostradamus (VN) another one of our systems, which is the "finder" of explosive stock opportunities. Every day, it generates a small list of targets, screening out penny stocks and illiquid stocks. This group is them refined down by our master traders to determine the aggressive short term trade for the day, and the high-quality institutional grade blue chip trade for the day. These stocks are run through BITE to deliver the specific price target, the time frame, and the "resolution date" - the date by which the stock should have moved to or above the target.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography variant='body1' fontWeight='bold' textAlign='center' paragraph>
                  God Bless America and All the Carnivores.
                </Typography>

                <FullScreenImg imgSrc={IMAGES.flagImg} />

                <StyleHelper.VerticalSpace />

                <Typography variant='body1' fontWeight='bold' fontStyle='italic' textAlign='center'>
                  "Congress shall make no law…abridging the freedom of speech…"
                </Typography>
                <Typography variant='body1' fontWeight='bold' textAlign='center'>
                  — From the First Amendment to the Constitution
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Box textAlign='center'>
                  <Typography variant='h6' fontWeight='bold' paragraph>
                    SIGN UP FOR THE MARCH ACADEMY TODAY
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    IT'S SELLING OUT FAST
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    ASK GODFATHER AND JENN ABOUT HOW TO GET ACADEMY FOR FREE WITH YOUR DIRECT AXXESS SUBSCRIPTION!
                  </Typography>
                  <Typography variant='body1' fontWeight='bold' paragraph>
                    WHAT? FREE ACADEMY?
                  </Typography>

                  <StyleHelper.VerticalSpace />

                  <YouTubeEmbed videoId='prIPKuq8wLU' title='Academy Promo Video' />

                  <StyleHelper.VerticalSpace />

                  <Link href='mailto:SUPPORT@CARNIVORETRADING.COM' fontWeight='bold'>
                    SUPPORT@CARNIVORETRADING.COM
                  </Link>
                  <Typography variant='body1' fontWeight='bold'>
                    CONTACT GODFATHER AND JENN TODAY!
                  </Typography>
                </Box>
              </PremiumSection>
            </Box>

            {/* Closing */}
            <PremiumSection title='Until Tomorrow Carnivores!' noPaper>
              <Box textAlign='center'>
                <FullScreenImg imgSrc={IMAGES.fishingImg} />

                <StyleHelper.VerticalSpace />

                <Typography variant='h6' fontWeight='bold'>
                  DUTCH AND TRADER Z
                </Typography>
                <Typography variant='body1' fontWeight='bold'>
                  Fishing for great growth companies!
                </Typography>
              </Box>
            </PremiumSection>

            {/* Trader Z Section */}
            <Box id='premium-trader-z'>
              <PremiumSection title="Trader Z Thoughts">
                <Typography variant='h6' fontWeight='bold' gutterBottom>
                  What Happened Today
                </Typography>

                <Typography variant='body1' paragraph>
                  The S&P 500 moved higher on Wednesday Carnivores, with leadership back in key tech and AI names, as traders digested a modestly hawkish‑but‑patient set of Fed minutes and a firm move in oil. The index climbed 0.56% to 6,881.31, while the Nasdaq Composite added 0.78% to 22,753.63, reflecting renewed strength in large‑cap growth. The Dow Jones Industrial Average rose 0.26% to 49,662.66, as a mix of mega‑cap tech and select industrials and financials supported the tape. The Russell 2000 lagged but still participated, finishing modestly higher on the day as small caps continued to trade like a high‑beta expression of the "rates drifting lower, growth not collapsing" narrative rather than a clear new leadership group. Sector breadth was constructive but not explosive, with 8 of 11 S&P sectors closing in the green; Energy, Consumer Discretionary, Communication Services and Information Technology led on the back of AI, semis, and mega‑cap platform strength, while more defensive pockets such as Utilities and Consumer Staples were flat to slightly positive and traditional defensives like Real Estate struggled under the weight of rate confusion and AI‑disruption fears in property and related services.
                </Typography>

                <Typography variant='body1' paragraph>
                  Within tech, the AI complex reasserted itself. Nvidia gained about 1.6% after Meta reiterated that it intends to use millions of Nvidia chips in its next‑generation AI data‑center build‑out, reinforcing the view that AI infrastructure CAPEX remains on a multi‑year trajectory rather than nearing a cliff. Amazon rallied nearly 2% after filings showed Bill Ackman's Pershing Square increased its stake by roughly 65% in the fourth quarter, making the stock the fund's third‑largest holding and helping it extend its rebound after snapping a nine‑day losing streak. Micron surged more than 5% as David Tepper's Appaloosa added to an already large position and turned more constructive on memory and related plays, which traders read as another tell that the AI hardware and memory cycle still has room to run. Under the surface, the move was not just about the "usual suspects": less‑headline‑driven industrial‑tech names, such as Trimble, tacked on gains of around 2%, supporting the idea that the market's "broadening out" theme remains alive even on days when mega‑cap AI grabs the spotlight.
                </Typography>

                <Typography variant='body1' paragraph>
                  Remember Carnivores the Fed minutes are rear view and reinforced the a "higher for longer, but flexible" stance that the Fed had back in January. Officials unanimously backed holding the policy rate at 3.5%–3.75% in January, but the discussion revealed a split about what comes next: several participants see scope for further cuts if inflation continues to track lower, while others argued for leaving rates unchanged "for some time," and a smaller group wanted to keep the possibility of a hike on the table if disinflation stalls. That mix underscores a committee that is data‑dependent and in no rush to ease aggressively, but also not looking to re‑tighten preemptively. We think they are leaning too hard on the "risk of re‑acceleration" narrative; the run of CPI/PPI data has clearly cooled, and the Fed is fighting the last war while market‑based measures of inflation and real‑time pricing point to continued disinflation. In our view, Friday's PCE report is likely to confirm that trend—a softer core print that underscores fading inflation pressure and ultimately forces the Fed toward a more dovish posture than these minutes imply. This Fed has a track record of being late Carnivores.
                </Typography>

                <Typography variant='body1' paragraph>
                  Outside of equities and the Fed, commodities added another layer of complexity. Oil prices jumped as traders reacted to headlines that Iran had not satisfied U.S. "red lines" in nuclear talks and that military action remained a possibility, re‑injecting a geopolitical risk premium into crude. That supported Energy but also reminded the market that an upside surprise in oil remains one of the cleaner paths to a re‑acceleration in headline inflation if tensions escalate. Meanwhile, software remained the weak link: after an already brutal AI‑disruption‑driven drawdown, the group came under additional pressure intraday, reflecting lingering fears that AI tools will compress pricing power and undermine traditional SaaS models even as other parts of the tech stack rallied. Taken together, Wednesday's tape showed a market that is still willing to pay for AI, chips, and the strongest platforms, is cautiously constructive on small caps and cyclicals, but remains extremely discerning—and, in the case of software, perhaps overly pessimistic—as it parses a Fed that is not yet ready to admit how far inflation has already come in the right direction.
                </Typography>

                <StyleHelper.DoubleVerticalSpace />

                <Typography variant='h6' fontWeight='bold' gutterBottom>
                  Index Charts
                </Typography>

                <ChartTabs
                  tabs={[
                    { label: 'QQQ Daily', imgSrc: IMAGES.qqqDaily },
                    { label: 'SPY Daily', imgSrc: IMAGES.spyDaily },
                    { label: 'IWM Daily', imgSrc: IMAGES.iwmDaily },
                    { label: 'DIA Daily', imgSrc: IMAGES.diaDaily }
                  ]}
                />

                <StyleHelper.DoubleVerticalSpace />

                <Typography variant='h6' fontWeight='bold' gutterBottom>
                  Vector Nostradamus Notes
                </Typography>

                <Typography variant='body1' paragraph>
                  Vector Nostradamus notes that both the DIA and IWM are above all key moving averages. VN wants to see follow-through recovery in the SPY and now in the QQQs after today's reversal. The first order of business is for the SPY and QQQs to retake their 50-day moving averages. VN is still watching for a breakout back above key resistance in ALL the major indexes to ultimately run to new highs, but first we will have to reclaim key 50-day moving averages in the SPY and QQQs. Currently only the IWM and DIA are still signaling that the trend is still strong, but VN is now flashing oversold buy signals in software and technology.
                </Typography>

                <Typography variant='body1' paragraph>
                  Vector Nostradamus has this market at just 2/5 in the trend model Carnivores. The number of advancers vs. decliners was ok today at 1.55 to 1 in favor of advancers. VN's favorite sectors are telecom/fiber optics, computer data storage, biomed, biotech, semiconductors and semiconductor equipment, gold and silver mining, and mining-metal and ores. Oil and gas is on the rise.
                </Typography>

                <Paper elevation={2} sx={{ p: 2, backgroundColor: 'rgba(226,90,11,0.05)', borderLeft: '4px solid #e25a0b' }}>
                  <Typography variant='body1' fontWeight='bold'>
                    Join us each Monday at 11:00 AM EST for our weekly call. Some of you still aren't doing it! If you can't make it, check out the re-play on the dashboard (Past Calls Tab on the Dashboard) or via the link we email to you directly. Don't miss this important part of your Carnivore subscription, so that you can have the full context of our research and trading ideas.
                  </Typography>
                </Paper>
              </PremiumSection>
            </Box>

            {/* Stock of the Day */}
            <Box id='premium-stock-of-day'>
              <PremiumSection title='Vector Nostradamus Stock of the Day'>
                <Typography variant='body1' fontStyle='italic' paragraph>
                  Every day we find the 4% of the market that is driving performance. The VN "stock of day" is an idea generated by our proprietary system at the close each day. It is a stock demonstrating all the characteristics we look for: it is rising, in the right sector, scores well within our ranking systems, has completed one of our favorite patterns, and is often at/near the pivot entry point. It is meant to demonstrate everything you should be looking for in a trade setup and is often being traded in one of our hypothetical portfolios.
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant='h5' fontWeight='bold' color='primary' gutterBottom>
                  VN Stock of the Day: CNX Resources (NYSE: CNX)
                </Typography>

                <Typography variant='body1' paragraph>
                  The VN stock of the day is CNX Resources (NYSE: CNX). CNX Resources is an Appalachia‑focused independent E&P that develops, produces, and gathers natural gas, with an explicit pitch as an "ultra‑low carbon intensive" gas producer plus associated midstream and tech around its acreage and methane capture. The company controls roughly 9.7 Tcf equivalent of proved reserves in the basin and leans heavily on disciplined capital allocation—keeping production roughly flat, aggressively hedging, and using free cash flow for buybacks, debt reduction, and now opportunistic refinancing.
                </Typography>

                <Typography variant='body1' paragraph>
                  Recent financial performance has been strong. In Q4 2025, CNX beat Street estimates handily with operating EPS of about $0.68 versus roughly $0.40 expected, up almost 20% year‑on‑year, on revenue of $419 million that exceeded consensus by more than 12% and grew nearly 9% versus the prior year. For full‑year 2025, revenue climbed about 18% to $1.73 billion, production volumes increased more than 7%, unit cash costs ticked lower, and free cash flow reached roughly $646 million on about $495 million of capex. Management has been returning a lot of that to shareholders: the company has spent roughly $1.9 billion on buybacks over 21 quarters, shrinking the share count materially and helping drive per‑share value even in a choppy gas tape, while also paying down and refinancing debt. Guidance for 2026 calls for essentially flat production (~605–620 Bcfe), capex in the mid‑$500 millions, and free cash flow around $550 million with EBITDAX in the low‑$1.3 billion range, signaling a steady "harvest" mode rather than volume‑chasing.
                </Typography>

                <Typography variant='body1' paragraph>
                  The stock has responded accordingly. CNX is trading around $40 with a market cap near $5.7 billion, not far off a 52‑week high of about $42 and up roughly 30–35% over the past year as the market has rewarded its free‑cash‑flow and buyback story. On today's numbers, you're looking at a P/E of about 10x on trailing EPS of $3.98, with the stock sitting comfortably above both its 50‑day ($37.86) and 200‑day ($33.64) moving averages, which confirms a solid intermediate uptrend rather than a speculative spike. That combination—double‑digit free‑cash‑flow yield, disciplined growth, visible capital returns, and a still‑single‑digit/low‑teens earnings multiple—helps explain why CNX and similar gas‑levered names screen as attractive in this tape.
                </Typography>

                <Typography variant='body1' paragraph>
                  Oil and gas names like CNX look particularly interesting here for a few reasons. First, the macro backdrop is quietly improving for the complex: oil has settled into the low‑$60s, U.S. gas pricing has bounced off the ugliest levels, and structural demand drivers (LNG exports, industrial and petrochemical load, and eventually more gas‑backed power for data centers/AI) argue for sustained volume growth even if prices hover near historical averages. Second, the group just went through its own de‑rating as investors chased AI and software, leaving many E&Ps trading at 8–11x earnings and high single‑digit free‑cash‑flow yields despite cleaner balance sheets, more disciplined capex frameworks, and less "drill at any cost" behavior than prior cycles. Third, CNX specifically has de‑risked a lot of its profile: heavy hedging, a flat production plan, and recent refinancing of 6.0% 2029 notes into new 5.875% 2034 paper extend duration and modestly lower coupon costs, while the tender offer for old notes and continued buybacks tighten the equity and debt stacks over time. In a market that's still nervous about stretched growth multiples and AI narratives, a gas‑weighted mid‑cap trading near 10x earnings with stable volumes, visible free cash flow and ongoing capital returns stands out as a relatively straightforward way to own real assets and optionality on higher gas and power demand without paying up for blue‑sky.
                </Typography>

                <StyleHelper.VerticalSpace />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
                      CNX Daily
                    </Typography>
                    <FullScreenImg imgSrc={IMAGES.cnxDaily} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
                      CNX Weekly
                    </Typography>
                    <FullScreenImg imgSrc={IMAGES.cnxWeekly} />
                  </Grid>
                </Grid>
              </PremiumSection>
            </Box>

            {/* Economy Section */}
            <PremiumSection title='The Economy is Accelerating'>
              <Typography variant='body1' paragraph>
                Rising business investment has quietly become the engine of this five‑year‑old U.S. expansion, and the latest data says that engine is not just intact, it is accelerating in a way that is distinctly bullish for stocks. A key gauge of capital spending – core orders for durable goods, which strip out volatile transportation and defense – rose 0.6% in December, its sixth straight monthly increase and the strongest annual advance in business investment in three years. That means companies are not behaving like we are late‑cycle and rolling over; they are behaving like they see real opportunity ahead and are willing to commit fresh capital to capture it. When you combine that with an equity market that has already digested tighter policy, lower inflation trends, and some sentiment washouts in key sectors, you get the backbone of a "higher for longer growth" story that supports higher earnings and, ultimately, higher equity prices.
              </Typography>

              <Typography variant='body1' paragraph>
                Under the surface, this investment boom is not random; it is being led by an AI‑ and automation‑driven capex cycle that is rippling out far beyond the obvious mega‑cap winners. Companies are in a frenzied race to integrate AI, cloud, automation, and data infrastructure into their operations, not as a science project but as a way to sustainably raise productivity and protect margins. Core capital‑goods orders rose almost 4% for all of 2025 after declining in both 2023 and 2024, and the entire back half of 2025 saw uninterrupted gains in investment – matching the longest positive streak in three years. That is exactly the pattern you want to see if you're bullish on a durable earnings cycle: not just consumer spending carrying the load, but businesses reinvesting aggressively in the tools that let them sell more per worker, automate lower‑value tasks, and scale without blowing out cost structures. In equity‑market terms, that is rocket fuel for both the "AI infrastructure" complex and the broader ecosystem of semis, industrial tech, software, and services levered to this spend.
              </Typography>

              <Typography variant='body1' paragraph>
                The best part for risk assets is that this capex boom looks set to extend, not fade, in 2026. AI leaders and their suppliers are talking about hundreds of billions of dollars in cumulative spend on chips, racks, data centers, connectivity, and adjacent infrastructure over the next several years. That money does not just land on the balance sheet of one or two chipmakers; it filters into electrical equipment, construction, industrial software, manufacturing tech, logistics, and the whole supply chain that makes modern compute possible. We're already seeing this in the data: investment rose steadily through the final six months of 2025, even as tariffs, election noise, and higher rates could have spooked CFOs into freezing budgets. Instead, they leaned in. That behavior is profoundly bullish for equities because it means corporate America is voting with its checkbook on a higher potential growth path, and those dollars ultimately show up as higher revenues and earnings for the public companies that build, ship, install, and run all of this gear.
              </Typography>

              <Typography variant='body1' paragraph>
                Importantly, the headline durable‑goods numbers actually understate how strong the underlying trend is. December's total durable‑goods orders fell 1.4%, but that was almost entirely due to a drop in new aircraft contracts at Boeing – a category that is famously lumpy and takes years to translate into final deliveries. When you strip out transportation, orders rose nearly 1% in December, marking the ninth consecutive monthly increase. That persistent ex‑transport strength is what equity investors should care about: it says broad‑based business demand for long‑lived equipment like computers, machinery, tools, and industrial systems is firm and broadening. Historically, extended runs in core capital‑goods orders have been associated with strong forward performance for cyclicals, industrials, semis, and even small‑caps, because those are the parts of the market most levered to a real, not just nominal, pick‑up in investment and productivity.
              </Typography>

              <Typography variant='body1' paragraph>
                All of this is happening against a backdrop that, frankly, many economists thought would crush capex, not fuel it. High tariffs, political uncertainty, and a period of restrictive monetary policy were widely expected to make management teams more cautious. You did see a wobble in the middle of 2025 as tariffs bit and the election noise heated up, but the data now shows that slowdown was temporary – spending has since rebounded robustly. That resilience matters for stocks because it means corporate behavior is being driven less by macro hand‑wringing and more by hard, bottom‑up ROI calculations: if AI, automation, and reshoring investments pencil out, companies will spend even in a noisy environment. The "tariffs will kill investment" narrative has so far been overwhelmed by a bigger force: the perceived payoff from modernizing the capital stock.
              </Typography>

              <Typography variant='body1' paragraph>
                From a market perspective, the big picture could not be clearer: corporate America is acting like AI and related technologies are going to reshape how they organize, how they produce, and how many people they need per unit of output – and they are putting real capital behind that belief. That is exactly the kind of capex‑plus‑productivity cocktail that supports above‑trend GDP growth and a multi‑year earnings expansion, even if headline growth doesn't look spectacular quarter to quarter. One prominent economist summed it up well: the AI boom carried the business‑spending component of GDP through the first three quarters of 2025, and by late in the year, firms outside the pure tech space started to re‑engage, setting the stage for a noticeable pickup in investment outlays in 2026. That is his main justification for expecting above‑consensus economic performance this year – and if he's right, the equity market is still underpricing how strong the "real economy" backdrop could be.
              </Typography>

              <Typography variant='body1'>
                Put simply, this looks like the early‑to‑middle innings of a capex‑led expansion, not the end. Rising core investment, a broadening of spend beyond just the AI "winners," resilient activity despite tariffs and higher rates, and the promise of productivity gains all line up on the bullish side of the ledger for stocks. In that environment, pullbacks in cyclicals, industrials, semis, small‑caps, and even select software and infrastructure names look more like buyable shakeouts than the start of a new bear market. For equity traders, you want to be leaning into the areas that monetize this investment wave, not hiding from it.
              </Typography>
            </PremiumSection>
          </Box>

          {/* Disclaimers Section */}
          <Box id='premium-legal'>
            <Accordion
              sx={{
                border: '1px solid rgba(226,90,11,0.2)',
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  backgroundColor: 'rgba(226,90,11,0.05)',
                  '&:hover': { backgroundColor: 'rgba(226,90,11,0.1)' }
                }}
              >
                <Typography fontWeight='bold'>Legal Disclaimers</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant='body2' fontWeight='bold' paragraph>
                  WE DO NOT GIVE FINANCIAL ADVICE.
                </Typography>
                <Typography variant='body2' fontWeight='bold' paragraph>
                  WE ARE NOT FINANCIAL ADVISORS.
                </Typography>
                <Typography variant='body2' fontWeight='bold' paragraph>
                  NOTHING HEREIN IS TO BE CONSIDERED A RECOMMENDATION.
                </Typography>
                <Typography variant='body2' fontWeight='bold' paragraph>
                  ALWAYS CONSULT WITH YOUR OWN FINANCIAL INSTITUTION FOR YOUR SPECIFIC SITUATION.
                </Typography>
                <Typography variant='body2' fontWeight='bold' paragraph>
                  THIS SITE IS FOR ENTERTAINMENT AND INFORMATIONAL PURPOSES ONLY.
                </Typography>
                <Typography variant='body2' fontWeight='bold' paragraph>
                  OUR COMMENTS AND THE INFORMATION IN THE COMMENTARY SECTION IN PARTICULAR ARE PURELY OUR OPINIONS AND ARE NOT INTENDED TO BE RELIED UPON TO MAKE FINANCIAL DECISIONS.
                </Typography>
                <Typography variant='body2' fontWeight='bold' paragraph>
                  OUR POLICY ALWAYS HAS BEEN THAT IF YOU ARE NOT HAPPY WITH OUR SERVICE YOU CAN REQUEST A REFUND. WE MAKE NO GUARANTEES ON PERFORMANCE AND CAN'T DELIVER INDIVIDUALIZED ADVICE. WE DO NOT MANAGE YOUR MONEY OR KNOW YOUR PERSONAL SITUATION – BUT IF YOU ARE UNHAPPY WITH OUR SERVICE, LET US KNOW. WE ALWAYS GO THE EXTRA MILE TO TAKE CARE OF THE CARNIVORE FAMILY.
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant='body2' fontWeight='bold' paragraph>
                  CARNIVORE TRADING, LLC – IMPORTANT NOTICE
                </Typography>
                <Typography variant='body2' fontWeight='bold' paragraph>
                  Carnivore is not a registered investment adviser and operates legally pursuant to an exemption to registration for business and financial publications. Nothing in these written materials and nothing we say to you should be taken as personalized investment, legal, accounting or tax advice, or a recommendation to buy, hold, sell a security. You should consult a licensed securities professional before making any investment decisions.
                </Typography>
                <Typography variant='body2' fontWeight='bold' paragraph>
                  Past performance is no guarantee of future results. Inherent in any investment is the potential for loss. It should not be assumed that any investment in securities, companies, sectors or markets identified and described or discussed herein will be or were profitable. All information is current as of the date of herein and is subject to change without notice.
                </Typography>
                <Typography variant='body2' fontWeight='bold' paragraph>
                  The Carnivore Trading Portfolio is a hypothetical portfolio that is weighted and may use leverage in large quantities. These are not the returns of an actual fund or portfolio of stocks. Your results may vary significantly from the Carnivore Trading Portfolio due to many factors outside of our control. You understand and agree that Carnivore is not directing you as to what to buy or when or in what amounts, and that those decisions are yours and yours alone (along with any licensed securities professional you may engage) as are your results. You understand that investing in the stock market carries risk of loss and that in no manner have we led you to believe otherwise.
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant='body2' fontWeight='bold' paragraph>
                  *** Notes on How We Calculate Past Performance
                </Typography>
                <Typography variant='body2' paragraph>
                  ***The Carnivore Portfolio is a hypothetical and weighted portfolio. When we say "buy" it is a hypothetical purchase, an addition to the hypothetical portfolio. The Carnivore Portfolio uses leverage from time to time in large amounts and weightings can change rapidly and the positions may change rapidly as well. We rarely day trade. We do not trade options. Our picks are the same as those we use with all Carnivores in the text alerts. We don't buy or sell anything differently than what you see in the Instant Text Alerts. Our weightings in each recommendation may change rapidly and they can be very, very concentrated. At times we may also hold a large cash position and little or no stocks or other securities at all. When we say we are buying a stock, we are adding it to the portfolio. When we say we are selling a stock, we are taking it out of the portfolio. We never take any compensation from anyone for mentioning a stock. The only money we make is from the monthly fees from our subscribers. Therefore, our loyalty is clearly to our subscribers at all times. You will never see an advertisement for a fund or a company shilling its stock on our site. As the portfolio is hypothetical there can be no "front-running" of our subscribers. We analyze almost exclusively only high-quality stocks with a lot of liquidity and that are traded on major stock exchanges, maintain current filings with the SEC, and are mostly all followed by research on Wall Street, so there is ample available information out in the marketplace for our subscribers to research anything that we are discussing. We do NOT analyze options trades, analyze micro-cap stocks, penny stocks, or other "low quality" or esoteric securities or stocks. Carnivore Trading LLC is not an advisor or manager of other people's money and we DO NOT MAKE RECOMMENDATIONS OR OFFER ADVICE.
                </Typography>
                <Typography variant='body2' paragraph>
                  Your results may differ materially from those expressed or utilized by Carnivore Trading LLC due to any number of factors. As a provider of education and entertainment we do not have access to the personal trading accounts or brokerage statements of our subscribers and in no way have any control over what any of our subscribers buy or sell.
                </Typography>
                <Typography variant='body2' paragraph>
                  If you do not agree with any term or provision of our Terms and Conditions you should not use our site. Please be advised that your continued use of the site, services, content, or information shall indicate your consent and agreement to our Terms and Conditions.
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant='body2' fontWeight='bold' paragraph>
                  IMPORTANT – PLEASE READ
                </Typography>
                <Typography variant='body2' fontWeight='bold' fontStyle='italic' paragraph>
                  WE ANALYZE QUALITY, LIQUID, WELL FOLLOWED AND CURRENTLY REPORTING STOCKS ON MAJOR STOCK EXCHANGES.
                </Typography>
                <Typography variant='body2' fontWeight='bold' fontStyle='italic' paragraph>
                  WE PRIDE OURSELVES ON BEING A SERVICE THAT ANY QUALITY INSTITUTION CAN UTILIZE IN ITS OWN WAY.
                </Typography>
                <Typography variant='body2' fontWeight='bold' fontStyle='italic' paragraph>
                  WE MAKE NO RECOMMENDATIONS, WE ONLY SHARE WITH YOU OUR OPINIONS, OUR THOUGHTS, AND WHAT WE ARE SEEING IN THE MARKETS.
                </Typography>
                <Typography variant='body2' paragraph>
                  Content, news, research, tools, and securities symbols are for educational and illustrative purposes only and do not imply a recommendation or solicitation to buy or sell a particular security or to engage in any particular investment strategy. Companies that are not affiliated with us may supply some of the content. The source of all, third-party Content, in which Carnivore Trading LLC receives some sort of compensation, is clearly and prominently identified herein as "Sponsored by" or "Sponsored". We have not been involved in the preparation, adoption or editing of third party content and we do not endorse or approve such content. The information provided is not warranted as to completeness or accuracy and is subject to change without notice. The projections or other information regarding the likelihood of various investment outcomes are hypothetical in nature, are not guaranteed for accuracy or completeness, do not reflect actual investment results and are not guarantees of future results. All investments involve risk, losses may exceed the principal invested, and the past performance of a security, industry, sector, market, or financial product does not guarantee future results or returns. Before investing in a fund or ETF or stock, consider the fund's investment objectives, risks, charges, and expenses and your own investment tolerances and objectives and consult with a licensed financial professional although they may be dumber than a stump, so beware of that, too.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Grid>

        {/* Sticky Navigation */}
        <Grid
          item
          lg={3}
          sx={{
            display: { xs: 'none', lg: 'block' }
          }}
        >
          <PremiumNav items={PREMIUM_WRAP_NAV} />
        </Grid>
      </Grid>

      <ScrollToTopButton />
    </Container>
  )
}
